import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all duration-300 transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative overflow-hidden group",
  {
    variants: {
      variant: {
        default: "bg-[#6E3B63] text-[#F9EBEF] shadow-lg hover:scale-105 before:absolute before:inset-0 before:bg-[#590D0D] before:translate-x-[-100%] before:transition-transform before:duration-300 hover:before:translate-x-0",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-[#6E3B63]/30 text-[#6E3B63] hover:border-[#6E3B63] bg-transparent",
        secondary: "border border-[#6E3B63]/30 text-[#6E3B63] hover:border-[#6E3B63] bg-transparent rounded-full",
        ghost: "text-[#6E3B63] hover:bg-[#F9EBEF]",
        link: "text-primary underline-offset-4 hover:underline",
        white: "bg-white text-[#6E3B63] hover:bg-gray-50 shadow-lg font-bold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-full px-3",
        lg: "h-11 rounded-full px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {asChild ? children : <span className="relative z-10 flex items-center gap-2">{children}</span>}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
